import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { addDays, parseISO, format, isWeekend, differenceInCalendarDays } from "date-fns";
import { nb } from "date-fns/locale";
import { Truck, HardHat, Flag, CheckCircle2, Clock, ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { label } from "@/lib/labels";

/* ------------------------------------------------------------------ model ---- */

const FELT = ["montasje", "demontasje", "klargjoring", "inspeksjon"];

const kindOf = (e) => (e.type === "transport" ? "transport" : FELT.includes(e.type) ? "felt" : e.type === "prosjekt_start" ? "start" : e.type === "prosjekt_slutt" ? "slutt" : e.type === "tilbud_utloper" ? "frist" : "annet");

const STYLE = {
  transport: { icon: Truck, cls: "bg-primary/10 text-primary ring-1 ring-inset ring-primary/15 hover:bg-primary/15" },
  transit: { icon: Truck, cls: "bg-primary text-primary-foreground shadow-[0_2px_8px_-2px_rgba(38,112,242,0.6)] hover:bg-primary/90" },
  unconfirmed: { icon: Truck, cls: "bg-amber-500/10 text-amber-800 ring-1 ring-inset ring-amber-500/20 hover:bg-amber-500/15 dark:text-amber-200" },
  felt: { icon: HardHat, cls: "bg-amber-500/10 text-amber-900 ring-1 ring-inset ring-amber-500/15 hover:bg-amber-500/15 dark:text-amber-100" },
  start: { icon: Flag, cls: "bg-emerald-500/10 text-emerald-800 ring-1 ring-inset ring-emerald-500/20 hover:bg-emerald-500/15 dark:text-emerald-200" },
  slutt: { icon: CheckCircle2, cls: "bg-muted text-foreground/70 ring-1 ring-inset ring-black/[0.04] hover:bg-muted/80" },
  frist: { icon: Clock, cls: "bg-rose-500/10 text-rose-700 ring-1 ring-inset ring-rose-500/20 hover:bg-rose-500/15 dark:text-rose-200" },
  annet: { icon: Clock, cls: "bg-muted text-foreground/70" },
};

const styleOf = (e) => {
  const k = kindOf(e);
  if (k === "transport") return e.status === "pa_vei" ? STYLE.transit : ["planlagt", "bestilt"].includes(e.status) ? STYLE.unconfirmed : STYLE.transport;
  return STYLE[k] || STYLE.annet;
};

const text = (e) => {
  const k = kindOf(e);
  if (k === "transport") return { main: e.module_count ? `${e.module_count} mod. ${e.direction === "retur" ? "retur" : "ut"}` : e.direction === "retur" ? "Retur" : "Utlevering", sub: e.status === "pa_vei" ? "På vei" : ["planlagt", "bestilt"].includes(e.status) ? "Ikke bekreftet" : e.time_window || "" };
  if (k === "felt") return { main: label(e.type), sub: e.module_count ? `${e.module_count} mod.` : (e.title || "").split(" – ")[0] };
  if (k === "start") return { main: "Oppstart", sub: e.ref || "" };
  if (k === "slutt") return { main: "Avslutning", sub: e.ref || "" };
  if (k === "frist") return { main: "Tilbud utløper", sub: e.ref || "" };
  return { main: e.title, sub: "" };
};

const eventUrl = (e) =>
  e.entity_type === "project" ? `/prosjekter/${e.entity_id}` : e.entity_type === "quote" ? `/tilbud/${e.entity_id}` : e.entity_type === "transport" ? `/service?tab=transport&id=${e.entity_id}` : null;

/** Greedy interval packing → each event gets a sub-lane so overlapping spans stack instead of colliding. */
const pack = (spans) => {
  const lanes = [];
  const sorted = [...spans].sort((a, b) => a.s - b.s || b.e - a.e);
  for (const sp of sorted) {
    let li = lanes.findIndex((end) => end < sp.s);
    if (li === -1) {
      lanes.push(sp.e);
      li = lanes.length - 1;
    } else lanes[li] = sp.e;
    sp.lane = li;
  }
  return lanes.length;
};

const ROW_H = 30;

/* ------------------------------------------------------------------- view ---- */

const Block = ({ sp, dayPct }) => {
  const navigate = useNavigate();
  const e = sp.ev;
  const st = styleOf(e);
  const Icon = st.icon;
  const t = text(e);
  const url = eventUrl(e);
  const wide = sp.e - sp.s >= 1;
  return (
    <button
      type="button"
      onClick={() => url && navigate(url)}
      title={`${e.title}${e.time_window ? ` · ${e.time_window}` : ""}${e.assigned_name ? ` · ${e.assigned_name}` : ""}`}
      className={cn("absolute flex h-[26px] items-center gap-1.5 overflow-hidden rounded-lg px-2 text-left text-[11.5px] leading-none transition-[background-color,transform] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50", st.cls, url ? "cursor-pointer" : "cursor-default")}
      style={{ left: `calc(${sp.s * dayPct}% + 3px)`, width: `calc(${(sp.e - sp.s + 1) * dayPct}% - 6px)`, top: 8 + sp.lane * ROW_H }}
      data-testid="dashboard-event"
      data-event-type={e.type}
    >
      <Icon className="h-3.5 w-3.5 shrink-0 opacity-80" strokeWidth={1.75} aria-hidden />
      <span className="truncate font-medium">{t.main}</span>
      {t.sub && <span className={cn("truncate opacity-75", !wide && "hidden xl:inline")}>· {t.sub}</span>}
    </button>
  );
};

/**
 * The week as an operations flow: one row per project, its transports / field work / milestones drawn as blocks on a
 * seven-day axis and tied together with a hairline, so the reader sees how the week hangs together for each site.
 */
export const WeekTimeline = ({ today, events = [], className, maxRows = 8 }) => {
  const navigate = useNavigate();
  const days = useMemo(() => {
    const start = parseISO(today);
    return Array.from({ length: 7 }, (_, i) => ({ iso: format(addDays(start, i), "yyyy-MM-dd"), day: addDays(start, i), i }));
  }, [today]);

  const rows = useMemo(() => {
    const start = parseISO(today);
    const byKey = new Map();
    for (const e of events) {
      const endIdx = differenceInCalendarDays(parseISO(e.date), start);
      const startIdx = e.start_date ? Math.min(endIdx, differenceInCalendarDays(parseISO(e.start_date), start)) : endIdx;
      if (endIdx < 0 || startIdx > 6) continue;
      const sp = { ev: e, s: Math.max(0, startIdx), e: Math.min(6, endIdx) };
      const k = kindOf(e);
      const key = k === "frist" ? "__frister" : e.project_id || e.project_name || "__annet";
      if (!byKey.has(key)) {
        const full = e.project_name || "";
        const cut = full.indexOf(" – ");
        const name = k === "frist" ? "Tilbudsfrister" : cut > 0 ? full.slice(cut + 3) : full || "Annet";
        const customer = k === "frist" ? null : cut > 0 ? full.slice(0, cut) : null;
        byKey.set(key, { key, name, customer, ref: k === "frist" ? null : e.ref, projectId: k === "frist" ? null : e.project_id, spans: [] });
      }
      byKey.get(key).spans.push(sp);
    }
    const out = [...byKey.values()].map((r) => {
      r.lanes = pack(r.spans);
      r.first = Math.min(...r.spans.map((s) => s.s));
      r.last = Math.max(...r.spans.map((s) => s.e));
      r.hot = r.spans.some((s) => s.ev.status === "pa_vei" || s.s === 0);
      return r;
    });
    out.sort((a, b) => (a.key === "__frister") - (b.key === "__frister") || b.hot - a.hot || a.first - b.first || b.spans.length - a.spans.length);
    return out;
  }, [events, today]);

  const visible = rows.slice(0, maxRows);
  const hidden = rows.length - visible.length;
  const dayPct = 100 / 7;
  const cols = "grid-cols-[224px_minmax(0,1fr)]";

  if (!rows.length) {
    return (
      <div className={cn("rounded-2xl bg-muted/40 px-6 py-10 text-center text-[12.5px] text-muted-foreground dark:bg-muted/20", className)} data-testid="dashboard-timeline">
        Ingen planlagte hendelser de neste sju dagene.
      </div>
    );
  }

  return (
    <div className={cn("-mx-5 overflow-x-auto px-5 scrollbar-thin sm:mx-0 sm:px-0", className)} data-testid="dashboard-timeline">
      <div className="min-w-[960px]">
        {/* day axis */}
        <div className={cn("grid", cols)}>
          <div />
          <div className="relative grid grid-cols-7">
            {days.map((d) => {
              const we = isWeekend(d.day);
              return (
                <div key={d.iso} className={cn("flex items-baseline gap-1.5 px-3 pb-2 text-[12px] leading-4", d.i === 0 ? "text-primary" : we ? "text-muted-foreground/70" : "text-foreground/80")} data-testid={`agenda-day-${d.iso}`}>
                  <span className={cn("first-letter:uppercase", d.i === 0 && "font-medium")}>{d.i === 0 ? "I dag" : d.i === 1 ? "I morgen" : format(d.day, "EEEE", { locale: nb })}</span>
                  <span className="text-muted-foreground tabular">{format(d.day, "d. MMM", { locale: nb })}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl bg-muted/40 dark:bg-muted/20">
          {visible.map((r, ri) => (
            <div key={r.key} className={cn("grid", cols, ri > 0 && "border-t border-border/60 dark:border-white/5")} data-testid={`timeline-row-${r.key === "__frister" ? "frister" : "project"}`}>
              {/* project label */}
              <div className="flex min-w-0 items-start gap-2.5 px-4 py-3">
                <span className={cn("mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full", r.key === "__frister" ? "bg-rose-500" : r.hot ? "bg-primary" : "bg-slate-400")} aria-hidden />
                <span className="min-w-0 leading-tight">
                  {r.projectId ? (
                    <button type="button" onClick={() => navigate(`/prosjekter/${r.projectId}`)} className="block max-w-full truncate text-left text-[12.5px] font-medium transition-colors duration-150 hover:text-primary focus-visible:text-primary focus-visible:outline-none" data-testid="timeline-project-link">
                      {r.name}
                    </button>
                  ) : (
                    <span className="block truncate text-[12.5px] font-medium">{r.name}</span>
                  )}
                  <span className="block truncate text-[11px] text-muted-foreground tabular">
                    {r.customer ? `${r.customer} · ` : r.ref ? `${r.ref} · ` : ""}
                    {r.spans.length} {r.spans.length === 1 ? "hendelse" : "hendelser"}
                  </span>
                </span>
              </div>
              {/* seven-day track */}
              <div className="relative" style={{ minHeight: 16 + r.lanes * ROW_H }}>
                {/* day grid + today tint */}
                {days.map((d) => (
                  <span key={d.iso} className={cn("pointer-events-none absolute inset-y-0 border-l border-border/40 dark:border-white/5", d.i === 0 && "bg-primary/[0.05] dark:bg-primary/[0.09]", isWeekend(d.day) && d.i !== 0 && "bg-foreground/[0.015]")} style={{ left: `${d.i * dayPct}%`, width: `${dayPct}%` }} aria-hidden />
                ))}
                {/* connector between first and last activity */}
                {r.last > r.first && <span className="pointer-events-none absolute h-px bg-foreground/15" style={{ left: `calc(${r.first * dayPct}% + ${dayPct / 2}%)`, width: `calc(${(r.last - r.first) * dayPct}%)`, top: 8 + 13 }} aria-hidden />}
                {r.spans.map((sp, k) => (
                  <Block key={`${sp.ev.type}-${sp.ev.entity_id}-${k}`} sp={sp} dayPct={dayPct} />
                ))}
              </div>
            </div>
          ))}
          {hidden > 0 && (
            <div className="border-t border-border/60 px-4 py-2.5 text-[12px] dark:border-white/5">
              <button type="button" onClick={() => navigate("/planlegging")} className="inline-flex items-center gap-1 text-muted-foreground transition-colors duration-150 hover:text-foreground" data-testid="timeline-more">
                +{hidden} {hidden === 1 ? "prosjekt" : "prosjekter"} til denne uken <ArrowUpRight className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
